/**
 * Normalized user preferences shape stored in profiles.preferences jsonb.
 */

import { deepMergeSettings } from "./organizationSettings";

export const DEFAULT_USER_PREFERENCES = Object.freeze({
  ashley: {
    voiceFirstNavigation: true,
  },
  appearance: {
    theme: "system",
  },
});

export const APPEARANCE_THEME = Object.freeze({
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
});

export function mergeUserPreferences(base, patch) {
  return deepMergeSettings(base || DEFAULT_USER_PREFERENCES, patch);
}

export function profileRowToUserPreferences(row) {
  const raw = row?.preferences;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...DEFAULT_USER_PREFERENCES };
  }
  return mergeUserPreferences(DEFAULT_USER_PREFERENCES, raw);
}

/**
 * Dot-path read with fallback. Example: getEffectiveUserPreference(p, "ashley.voiceFirstNavigation", true)
 */
export function getEffectiveUserPreference(preferences, path, fallback = undefined) {
  const source = preferences || DEFAULT_USER_PREFERENCES;
  const parts = String(path).split(".");
  let cursor = source;

  for (const part of parts) {
    if (cursor == null || typeof cursor !== "object") {
      return fallback;
    }
    cursor = cursor[part];
  }

  return cursor === undefined ? fallback : cursor;
}

/**
 * "light" | "dark" | "system" (default). "system" means: follow the
 * device/browser color-scheme preference (applied via a CSS media query,
 * not JS) — the caller does not need to resolve it further.
 */
export function getAppearanceTheme(preferences) {
  const source = preferences || DEFAULT_USER_PREFERENCES;
  const theme = source.appearance?.theme;
  return theme === APPEARANCE_THEME.LIGHT || theme === APPEARANCE_THEME.DARK
    ? theme
    : APPEARANCE_THEME.SYSTEM;
}

/**
 * When true (default), tap opens Voice Assistant and press-and-hold opens Quick Menu.
 * When false, those actions are reversed.
 */
export function isVoiceFirstNavigation(preferences) {
  const source = preferences || DEFAULT_USER_PREFERENCES;
  const ashley = source.ashley || {};

  if (Object.prototype.hasOwnProperty.call(ashley, "voiceFirstNavigation")) {
    return ashley.voiceFirstNavigation !== false;
  }

  // Legacy key: disabling voice assistant mapped to quick-menu-first navigation.
  if (ashley.voiceAssistantEnabled === false) {
    return false;
  }

  return true;
}
